import {ChangeEvent, FC, useContext, useEffect, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {useLocation, useNavigate} from 'react-router-dom';
import {Search} from '@mui/icons-material';
import {GridColumns} from '@mui/x-data-grid';
import {Box, OutlinedInput, Tooltip} from '@mui/material';
import {Table} from '../../tables/Table';
import {TableLayout} from '../../tables/TableLayout';
import {TransactionDTO} from '../../../types/transactions';
import {EmptyTable} from '../../tables/EmptyTable';
import {useBusinessId, useAppSelector} from '../../../app/hooks';
import DashboardLayoutContext from '../../../layouts/DashboardLayout/DashboardLayout.context';
import {chargingSessionsTableColumns} from './ChargingSessionsTable.tableConfig';
import {DateBasisToggle} from 'src/components/common/DateBasisToggle';
import {FilterBar, FilterBarField} from 'src/components/common/FilterBar';
import {DateSelect} from 'src/components/common/DateSelect';
import {searchInputSx} from './ChargingSessionsTable.sx';
import {periodFieldSx} from 'src/components/common/FilterBar/FilterBar.sx';
import {ChargingSessionsTableCard} from './ChargingSessionsTableCard';
import {CardList} from 'src/components/common/CardList';
import {chargersApi, locationsApi, tagsApi, usersApi} from 'src/api';
import {AMPAutocomplete} from 'src/components/common/AMPAutocomplete';
import {findAutocompleteValueById} from 'src/utils/helper';
import {BaseUserInfo} from 'src/types/users';
import {TransactionExportButton} from '../TransactionExportButton';
import {DateFilterType} from 'src/components/common/TimeSelect/TimeSelect.types';

// Helper functions to format username based on a user type
const formatUserName = (user: BaseUserInfo): string => {
    if (!user) return '';

    let name = '';

    switch (user.userType) {
        case 'PRIVATE_DRIVER':
            name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
            break;

        case 'COMPANY':
            name = user.company?.name || '';
            break;

        case 'EMPLOYEE':
            if (user.employee) {
                // If an employee has first and last name, use them
                if (user.employee.firstName && user.employee.lastName) {
                    name = `${user.employee.firstName} ${user.employee.lastName}`;
                } else {
                    // If an employee doesn't have a name, use the company name
                    name = user.employee.company?.name || '';
                }
            }
            break;

        default:
            // Fallback for backward compatibility
            name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }

    // If name is still empty, use email address
    return name || user.emailAddress || '';
};

/** Which sessions the list holds: every one, those still running, or those that have ended. */
export type SessionStatus = 'all' | 'active' | 'completed';

/** The request's `active` flag for a status: absent means both. */
export const activeFlagOf = (status: SessionStatus | undefined): boolean | undefined =>
    !status || status === 'all' ? undefined : status === 'active';

interface ChargingSessionsTableProps {
    data: TransactionDTO[];
    page: number;
    totalPages: number;
    /** Total rows matching the filters, for the heading. Optional: not every caller knows it. */
    totalElements?: number;
    perPage: number;
    handleChangeRowsNumber: (e: ChangeEvent<HTMLInputElement>) => void;
    handleChangePage: (page: number) => void;
    handleNameChange: () => (e: ChangeEvent<HTMLInputElement>) => void;
    searchValue: string;
    handleSetDates: (newFrom: number, newTo: number, field: string) => () => void;
    handleChangeCurTime: (index: number) => void;
    curTime: number;
    locationId?: number;
    chargerId?: number;
    userId?: number;
    userType?: 'COMPANY' | 'PRIVATE_DRIVER';
    selectedCompanyId?: number;
    tagId?: number;
    onLocationChange: (locationId: number | null) => void;
    onChargerChange: (chargerId: number | null) => void;
    onUserChange: (userId: number | null, userType?: 'COMPANY' | 'PRIVATE_DRIVER', companyId?: number) => void;
    onTagChange: (tagId: number | null) => void;
    hideUserFilter?: boolean;
    hideAllFiltersExceptDate?: boolean;
    startFrom?: number;
    startTo?: number;
    dateFilterType?: DateFilterType;
    onDateFilterTypeChange?: (type: DateFilterType) => void;
    /** Set by the tiles above the list; here it only locks the basis and narrows the export. */
    status?: SessionStatus;
}

export const ChargingSessionsTable: FC<ChargingSessionsTableProps> = ({
                                                                          data,
                                                                          handleChangeRowsNumber,
                                                                          handleChangePage,
                                                                          page,
                                                                          totalPages,
                                                                          totalElements,
                                                                          handleNameChange,
                                                                          searchValue,
                                                                          perPage,
                                                                          handleSetDates,
                                                                          handleChangeCurTime,
                                                                          curTime,
                                                                          locationId,
                                                                          chargerId,
                                                                          userId,
                                                                          userType,
                                                                          selectedCompanyId,
                                                                          tagId,
                                                                          onLocationChange,
                                                                          onChargerChange,
                                                                          onUserChange,
                                                                          onTagChange,
                                                                          hideUserFilter = false,
                                                                          hideAllFiltersExceptDate = false,
                                                                          startFrom,
                                                                          startTo,
                                                                          dateFilterType,
                                                                          onDateFilterTypeChange,
                                                                          status = 'all',
                                                                      }) => {
    // The business being worked in: the caller's own, or the one a platform administrator picked.
    // Reading `authInfo.business.id` directly was a crash for them - they have no business of
    // their own, and null has no id.
    const businessId = useBusinessId();
    const intl = useIntl();
    const navigate = useNavigate();
    const currentLocation = useLocation();
    const {authInfo} = useAppSelector((state) => state.common);
    const responsive = useContext(DashboardLayoutContext);

    // API hooks for fetching locations, chargers, users, and tags
    const [getLocations, locationsResponse] = locationsApi.useLazyGetLocationsQuery();
    const [getChargers, chargersResponse] = chargersApi.useLazyGetChargersQuery();
    const [getUsers, usersResponse] = usersApi.useLazyGetUsersQuery();
    const [getTags, tagsResponse] = tagsApi.useLazyGetTagsQuery();

    // Load locations on mount (only if filters are not hidden)
    useEffect(() => {
        if (businessId && !hideAllFiltersExceptDate) {
            void getLocations({
                params: {page: 0, size: 1000},
                data: {
                    businessId: businessId,
                    companyId: authInfo?.company?.id,
                },
            });
        }
    }, [businessId, hideAllFiltersExceptDate]);

    // Load chargers when locationId changes or on mount (only if filters are not hidden)
    useEffect(() => {
        if (businessId && !hideAllFiltersExceptDate) {
            void getChargers({
                params: {page: 0, size: 1000},
                data: {
                    businessId: businessId,
                    companyId: authInfo?.company?.id,
                    locationId: locationId, // Filter by location if selected
                },
            });
        }
    }, [businessId, locationId, hideAllFiltersExceptDate]);

    // Load users when locationId changes or on mount (only if filters are not hidden)
    useEffect(() => {
        if (businessId && !hideAllFiltersExceptDate) {
            void getUsers({
                params: {page: 0, size: 1000},
                data: {
                    businessId: businessId,
                    companyId: authInfo?.company?.id,
                    locationId: locationId, // Filter by location if selected
                },
            });
        }
    }, [businessId, locationId, hideAllFiltersExceptDate]);

    // Load tags when locationId changes or on mount (only if filters are not hidden)
    useEffect(() => {
        if (businessId && !hideAllFiltersExceptDate) {
            void getTags({
                params: {page: 0, size: 1000},
                data: {
                    businessId: businessId,
                    companyId: authInfo?.company?.id,
                    locationId: locationId, // Filter by location if selected
                },
            });
        }
    }, [businessId, locationId, hideAllFiltersExceptDate]);

    const processedLocations = useMemo(() => {
        const locations = locationsResponse?.data?.content ?? [];
        return locations.map((location) => ({
            id: location.id,
            label: location.name,
        }));
    }, [locationsResponse?.data?.content]);

    const processedChargers = useMemo(() => {
        const chargers = chargersResponse?.data?.content ?? [];
        return chargers.map((charger) => ({
            id: charger.id,
            label: charger.chargePointId,
        }));
    }, [chargersResponse?.data?.content]);

    const processedUsers = useMemo(() => {
        const users = usersResponse?.data?.content ?? [];
        return users.map((user) => ({
            id: user.id,
            label: formatUserName(user),
            userType: user.userType as 'COMPANY' | 'PRIVATE_DRIVER' | undefined,
            companyId: user.company?.id,
        }));
    }, [usersResponse?.data?.content]);

    const processedTags = useMemo(() => {
        const tags = tagsResponse?.data?.content ?? [];
        return tags.map((tag) => ({
            id: tag.id,
            label: tag.idTag,
        }));
    }, [tagsResponse?.data?.content]);

    const tableData: Array<TransactionDTO & { id: number }> = data
        ? data.map((item) => ({
            ...item,
            id: item?.id,
        }))
        : [];

    const businessArr: GridColumns = [];

    const handleRowClick = (params: any) => {
        navigate(`/app/charging-sessions/${params.row.id}`, {
            state: {backTo: currentLocation.pathname + currentLocation.search},
        });
    };

    /**
     * The filters, as data rather than as seven inline controls.
     *
     * Which of these exist depends on who is looking - a driver has none of them, an employee none -
     * so the row used to come out a different length for every audience and was designed for one of
     * them. Behind a button of fixed width the row is the same shape for everybody, and what is
     * actually set shows as chips, which is the part a reader wants at a glance.
     */
    /**
     * The filters, as data rather than as inline controls with their own copied styling.
     *
     * Which of these exist depends on who is looking - a driver has none of them, an employee none -
     * so the band is built from whatever is left rather than from four conditionals in the markup.
     */
    const filterFields: FilterBarField[] = hideAllFiltersExceptDate ? [] : [
        {
            id: 'location',
            control: (
                <AMPAutocomplete
                    options={processedLocations}
                    value={findAutocompleteValueById(processedLocations, locationId)}
                    input="location"
                    placeholder={intl.formatMessage({id: 'location'})}
                    onChange={(_e, newValue) => onLocationChange(newValue?.id ?? null)}
                />
            ),
        },
        {
            id: 'charger',
            control: (
                <AMPAutocomplete
                    options={processedChargers}
                    value={findAutocompleteValueById(processedChargers, chargerId)}
                    input="charger"
                    placeholder="Charge Point"
                    onChange={(_e, newValue) => onChargerChange(newValue?.id ?? null)}
                />
            ),
        },
        ...(hideUserFilter ? [] : [{
            id: 'user',
            control: (
                <AMPAutocomplete
                    options={processedUsers}
                    value={findAutocompleteValueById(processedUsers, userId)}
                    input="user"
                    placeholder="User"
                    onChange={(_e, newValue) => {
                        const user = newValue as {
                            id: number;
                            userType?: 'COMPANY' | 'PRIVATE_DRIVER';
                            companyId?: number
                        } | null;
                        onUserChange(user?.id ?? null, user?.userType, user?.companyId);
                    }}
                />
            ),
        }]),
        {
            id: 'tag',
            control: (
                <AMPAutocomplete
                    options={processedTags}
                    value={findAutocompleteValueById(processedTags, tagId)}
                    input="tag"
                    placeholder="RFID"
                    onChange={(_e, newValue) => onTagChange(newValue?.id ?? null)}
                />
            ),
        },
    ];

    // A running session has no stop time, so "active, by stop" is always empty. The basis is held on
    // Start while the Active tile is chosen, and says why instead of just refusing clicks.
    const basisLocked = status === 'active';

    const toolbar = (
        <FilterBar
            title={intl.formatMessage({id: 'chargingSessions'})}
            subtitle={typeof totalElements === 'number'
                ? intl.formatMessage({id: 'sessionsFound'}, {count: totalElements})
                : undefined}
            fields={filterFields}
            hasActiveFilters={Boolean(locationId || chargerId || userId || tagId)}
            onClearAll={() => {
                onLocationChange(null);
                onChargerChange(null);
                onUserChange(null);
                onTagChange(null);
            }}
            period={(
                <Box sx={periodFieldSx}>
                    <DateSelect
                        handleTimeChange={handleSetDates}
                        field="allSessions"
                        currentTimeInit={curTime}
                        handleSaveCurrentTimeIndex={handleChangeCurTime}
                        dense
                    />
                </Box>
            )}
            periodQualifier={onDateFilterTypeChange && (
                <Tooltip title={basisLocked ? intl.formatMessage({id: 'activeSessionsByStart'}) : ''}>
                    {/* A disabled button fires no hover, so the tooltip needs something that does. */}
                    <span>
                        <DateBasisToggle
                            value={basisLocked ? 'start' : dateFilterType ?? 'start'}
                            onChange={onDateFilterTypeChange}
                            disabled={basisLocked}
                        />
                    </span>
                </Tooltip>
            )}
            actions={(
                <>
                    <OutlinedInput
                        value={searchValue}
                        sx={searchInputSx}
                        color="secondary"
                        type="text"
                        placeholder={intl.formatMessage({id: 'search'})}
                        name="search"
                        onChange={handleNameChange()}
                        startAdornment={<Search/>}
                    />
                    <TransactionExportButton
                        filters={{
                            businessId: businessId,
                            // Use selected company's ID for COMPANY users, otherwise use current user's company
                            companyId: userType === 'COMPANY' && selectedCompanyId
                                ? selectedCompanyId
                                : authInfo?.company?.id,
                            employeeId: authInfo?.employee?.id,
                            keyword: searchValue?.length ? searchValue : undefined,
                            active: activeFlagOf(status),
                            ...(dateFilterType === 'stop' && !basisLocked
                                ? {stopFrom: startFrom, stopTo: startTo}
                                : {startFrom: startFrom, startTo: startTo}),
                            locationId,
                            chargerId,
                            // Pass userId for PRIVATE_DRIVER or when userId is set without userType
                            ...(userType === 'PRIVATE_DRIVER' && userId
                                ? {userId}
                                : userType !== 'COMPANY' && userId
                                    ? {userId}
                                    : {}),
                            tagId,
                        }}
                    />
                </>
            )}
        />
    );

    return (
        <TableLayout
            toolbar={toolbar}
            totalPages={totalPages}
            page={page}
            pageSize={perPage}
            onPageChange={handleChangePage}
            onPageSizeChange={handleChangeRowsNumber}
        >
            {responsive ? (
                <>
                    <CardList>
                        {tableData?.map((item) => (
                            <Box
                                key={item.id}
                                onClick={() =>
                                    navigate(`/app/charging-sessions/${item.id}`, {
                                        state: {backTo: currentLocation.pathname + currentLocation.search},
                                    })
                                }
                                sx={{cursor: 'pointer'}}
                            >
                                <ChargingSessionsTableCard
                                    data={item}
                                />
                            </Box>
                        ))}
                    </CardList>
                    {!tableData?.length && <EmptyTable/>}
                </>
            ) : (
                <Table
                    rows={tableData}
                    hideFooter
                    getRowId={(row) => row?.id}
                    columns={chargingSessionsTableColumns(businessArr, intl)}
                    onRowClick={handleRowClick}
                    sx={{
                        '& .MuiDataGrid-row': {
                            cursor: 'pointer',
                        },
                    }}
                />
            )}
        </TableLayout>
    );
};